import React, { useEffect, useMemo, useState } from "react";
import { Carousel } from "react-responsive-carousel";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import { Card, CardBody, CardHeader, CardTitle } from "reactstrap";
import { appState } from "../../AppState";
import { useOrders } from "../../hooks/useOrders";
import { useServices } from "../../hooks/useServices";
import { useTickets } from "../../hooks/useTickets";
import Enum from "../../utility/enum.js";
import { hasPolicies } from "../../utility/FeatureToggle";

const DynamicDashboardCardBodyCarousel = (props) => {
  const updateCarouselIndex = (index) => {
    props?.setCarouselIndex(index);
  };

  return (
    <Carousel
      selectedItem={props?.carouselIndex}
      showStatus={false}
      showThumbs={false}
      onChange={updateCarouselIndex}
      className="carousel-control-dots-abs"
    >
      {props?.components?.map((comp, index) => (
        <div key={index} className="h-100">
          {comp}
        </div>
      ))}
    </Carousel>
  );
};

const DynamicDashboardCard = (props) => {
  const [stateApp] = appState();
  const [matches, setMatches] = useState(null);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const noCard = useMemo(
    () =>
      typeof props?.noCard === "function"
        ? props?.noCard(stateApp?.env, stateApp?.clouds)
        : props?.noCard,
    [stateApp?.env, stateApp?.clouds, props?.noCard]
  );

  useEffect(() => {
    const m = props?.components?.filter((c) => {
      let matchCloud = false;
      let matchEnv = false;
      let matchPolicies = hasPolicies(c?.props?.policies, stateApp);
      let matchIntegration = true;
      for (let i = 0; i < (c?.props?.clouds || [])?.length; i++) {
        if (c?.props?.clouds[i] === Enum.CloudCategory.ALL) {
          matchCloud = true;
          break;
        }
        if (stateApp.clouds.includes(c?.props?.clouds[i])) {
          matchCloud = true;
          break;
        }
      }
      for (let i = 0; i < (c?.props?.envs || [])?.length; i++) {
        if (
          c?.props?.envs[i] === Enum?.Env?.ALL ||
          c?.props?.envs[i] === stateApp?.env
        ) {
          matchEnv = true;
          break;
        }
      }
      for (let i = 0; i < (c?.props?.integrations || [])?.length; i++) {
        if (!stateApp?.integrations?.includes(c?.props?.integrations[i])) {
          matchIntegration = false;
          break;
        }
      }
      return matchCloud && matchEnv && matchIntegration && matchPolicies;
    });
    setMatches(m);
  }, []);

  const hasContent = matches !== null && matches?.length > 0;
  const content =
    hasContent &&
    (matches?.length > 1 ? (
      <DynamicDashboardCardBodyCarousel
        components={matches}
        carouselIndex={carouselIndex}
        setCarouselIndex={setCarouselIndex}
      />
    ) : (
      matches[0]
    ));

  return (
    <>
      {hasContent && (
        <div className={props.className}>
          <div className={props.equalHeightCards ? "d-flex h-100" : ""}>
            {noCard ? (
              content
            ) : (
              <Card className="card-chart card-home">
                <CardHeader
                  className="card-header"
                  style={{
                    background: "white",
                    borderBottom: "none",
                    boxShadow: "none",
                  }}
                >
                  <CardTitle className="tria-card-title" tag="h3">
                    {props?.title}
                    {matches?.length > 1 && (
                      <span className="pull-right">
                        {carouselIndex + 1}/{matches?.length}
                      </span>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardBody>{content}</CardBody>
              </Card>
            )}
          </div>
        </div>
      )}
    </>
  );
};

const DynamicDashboardRow = (props) => {
  return (
    <>
      {props.cards.map((c, i) => {
        if (c?.nestedConfig) {
          return (
            <div key={i} className={c?.nestedConfig[0]?.className}>
              <DynamicDashboardComponents config={c?.nestedConfig} />
            </div>
          );
        } else {
          return (
            <DynamicDashboardCard
              key={`${c?.title}-${c?.components?.length}`}
              title={c?.title}
              className={c?.className}
              components={c?.components}
              noCard={c?.noCard}
              equalHeightCards={props?.equalHeightCards}
            />
          );
        }
      })}
    </>
  );
};

const CARD_ORDERS_TICKETS = "Ticketing & Orders";
const CARD_ORDERS = "Orders";
const CARD_TICKETS = "Tickets";
const CARD_BILLING = "Outstanding Balance At A Glance";


const DynamicDashboardComponents = (props) => {
  const [stateApp] = appState();
  const { orders, loading: ordersLoading } = useOrders();
  const { services, loading: servicesLoading } = useServices();
  const { tickets, loading: ticketsLoading } = useTickets();

  const hasOrders = !ordersLoading && orders && orders?.length > 0;
  const hasTickets = !ticketsLoading && tickets && tickets?.length > 0;
  const hasServices = !servicesLoading && services && services?.length > 0;
  const hasTicketPolicies = hasPolicies([Enum.Policies.TICKETS_VIEW,Enum.Policies.TICKETS_VIEW_ONLY], stateApp);
  const hasOrdersPolicies = hasPolicies([Enum.Policies.ORDERS_VIEW, Enum.Policies.ORDERS_WRITE], stateApp);
  const TICKETS_COMPONENT_INDEX = 0;
  const ORDERS_COMPONENT_INDEX = hasTicketPolicies ? 1 : 0; 

  const getTitle = () => {
    if (hasTicketPolicies && hasOrdersPolicies) {
      return CARD_ORDERS_TICKETS;
    }
    if (hasOrdersPolicies && !hasTicketPolicies) {
      return CARD_ORDERS;
    }
    if (hasTicketPolicies && !hasOrdersPolicies) {
      return CARD_TICKETS;
    }
    return null;
  }
  const components = useMemo(() => {
    return props.config.map((c, i) => {
      // filter cards based on data availability
      const filteredCards = c?.cards?.filter(card => {
        // for orders cards
        if (card?.title === CARD_ORDERS_TICKETS) {
          return hasOrdersPolicies || hasTicketPolicies;
        }

        // for services cards
        if (card?.title === CARD_BILLING) {
          return hasServices;
        }

        // all other cards are included
        return true;
      });

      // skip rendering this row if it has no cards after filtering
      if (filteredCards?.length === 0) {
        return null;
      }

      // filter out components that depend on async data
      const filteredCardsWithFilteredComponents = filteredCards?.map(card => {
        if (card?.title === CARD_ORDERS_TICKETS) {
          const filteredComponents = [];
          const title = getTitle();

          // Only include tickets component if there are tickets
          if (hasTickets && hasTicketPolicies) {
            filteredComponents.push(card?.components[TICKETS_COMPONENT_INDEX]);
          }
          // Only include orders component if there are orders
          if (hasOrders && hasOrdersPolicies) {
            filteredComponents.push(card?.components[ORDERS_COMPONENT_INDEX]);
          }

          // Return a new card object with filtered components
          return { 
            ...card,
            title: title,
            components: filteredComponents 
          };
        }

        return card;
      });

      return (
        <DynamicDashboardRow
          policies={c?.policies || []}
          cards={filteredCardsWithFilteredComponents}
          key={i}
          equalHeightCards={c?.equalHeightCards}
        />
      );
    }).filter(Boolean); // remove any null rows
  }, [
    props.config,
    hasOrders,
    hasServices,
    hasTickets,
  ]);

  return <>{components}</>;
};

export { DynamicDashboardComponents };